/**
 * Phase2WeeklyCell.tsx
 * FEAT-2026-0007 Phase 6C.2/6C.3/6C.6 + per-machine staffing (079) —
 * One cell in the Task×Week weekly allocation grid (the CLASSIC card presentation).
 *
 * For task T and week W this cell renders, GROUPED BY MACHINE BOOKING:
 *   - For each booking from task.Bookings that overlaps week W:
 *       1. A machine-time bar (slate blue) with a right-edge drag-extend handle.
 *       2. The employee PRESENCE rows whose MachineBookingKey is THAT booking —
 *          name + status chip + remove (×). Wave 3 (percent retirement): the
 *          0–100 WorkloadBar slider is GONE — staffing is pick-a-person; the
 *          person is busy (full day) on the days the booking covers. Per-day
 *          task-count chips live on the capacity grid below (Q1B), which is
 *          the surface that knows a person's cross-task day counts.
 *       3. A "+" that staffs THAT machine (passes machineBookingKey through).
 *   - A "Whole task" group for any legacy task-level rows (MachineBookingKey =
 *     null) when the task has ≥2 machines.
 *
 * 6C.6: when compareToBaseline is ON, a ghosted "also in baseline" sublabel
 * appears for users the BASELINE plan also staffs this week (presence, Q4A).
 *
 * ALL behavior (machine grouping, remove/picker state, handlers) lives in
 * useWeeklyCellModel — shared verbatim with the compact Board card
 * (Phase2BoardCell), so the two views can't drift.
 */

import React from 'react';
import {
  Alert,
  Box,
  IconButton,
  Snackbar,
  Tooltip,
  Typography,
} from '@mui/material';
import AddIcon from '@mui/icons-material/Add';
import CloseIcon from '@mui/icons-material/Close';
import WarningAmberIcon from '@mui/icons-material/WarningAmber';
import CheckCircleOutlineIcon from '@mui/icons-material/CheckCircleOutline';
import { alpha, useTheme } from '@mui/material/styles';

import { MachineBar } from './Phase2MachineBar';
import { taskWorkLabel } from '../../../../utils/bookingLabel';
import { useFlashUser } from './flashUserContext';
import { InlineAssigneePicker } from '../../tasks/InlineAssigneePicker';
import { useWeeklyCellModel, TASK_LEVEL_PICKER } from './useWeeklyCellModel';
import { ConfirmDialog } from '../../../../components/shared/ConfirmDialog';
import type { TaskWithBookings, TaskAssignee } from '../../../../services/scheduleTasks';
import type { AssignableUser } from '../../../../services/scheduleAssignments';

// ---------------------------------------------------------------------------
// Types
// ---------------------------------------------------------------------------

export interface Phase2WeeklyCellProps {
  task: TaskWithBookings;
  /** ISO Saturday 'YYYY-MM-DD' for this column. */
  weekStart: string;
  /** All assignments for this task (all weeks, all users). Filtered here by weekStart. */
  assignments: TaskAssignee[];
  /** Active scenario key. Forwarded to InlineAssigneePicker. */
  scenarioKey: string | null;
  /** Called after any assignment add/remove so parent reloads assignments for this task. */
  onAssignmentsChanged: (taskKey: string) => void;
  /** Called after any mutation that changes weekly allocation totals (capacity refetch). */
  onAllocationChanged?: () => void;
  /**
   * Escalate to full AssignEmployeesDialog (SkillGate / Overbook path). The third
   * arg carries the machine booking the picker was staffing so the dialog binds
   * the resolved assignment to the same machine (per-machine staffing, 079).
   */
  onEscalateToDialog: (
    task: TaskWithBookings,
    preselectUser?: AssignableUser,
    machineBookingKey?: string | null,
  ) => void;
  /**
   * 6C.6 Compare-to-Baseline: baseline (BASELINE/no-scenario) assignments for this task,
   * all weeks. Rendered as ghosted "baseline: N%" sublabels.
   */
  baselineAssignments?: Array<{ UserKey: string; WeekStart: string; Percent: number }>;
  /** When true, render the baseline ghost sublabels (6C.6). */
  compareToBaseline?: boolean;
  /** ISO stat-holiday dates — forwarded to the booking bar's mini week strip so a covered
   *  weekend / stat holiday greys out instead of reading as a worked day. */
  holidays?: string[];
}

// ---------------------------------------------------------------------------
// BaselineLabel — 6C.6 presence ghost sublabel (Wave 3: no percent)
// ---------------------------------------------------------------------------

/** Faint "also in baseline" label rendered when Compare to Baseline is ON. */
const BaselineLabel: React.FC = () => (
  <Typography
    variant="caption"
    sx={{
      fontSize: '0.62rem',
      color: 'text.disabled',
      opacity: 0.65,
      mt: -0.25,
      fontStyle: 'italic',
    }}
  >
    also in baseline
  </Typography>
);

// ---------------------------------------------------------------------------
// AssigneeRow — one employee presence row (name + status chip + remove)
// ---------------------------------------------------------------------------

interface AssigneeRowProps {
  assignee: TaskAssignee;
  isRemoving: boolean;
  onRemove: () => void;
  showBaseline: boolean;
}

const AssigneeRow: React.FC<AssigneeRowProps> = ({
  assignee,
  isRemoving,
  onRemove,
  showBaseline,
}) => {
  const theme = useTheme();
  const flashUser = useFlashUser();
  // New (this plan) vs already-there: a Tentative row is a DRAFT you're adding now;
  // a Committed row is already on the live schedule. Mirrors the machine bar's chips.
  const committed = assignee.Status === 'Committed';
  const accent = committed ? theme.schedule.committed : theme.schedule.employeeAlloc;
  return (
    <Box
      sx={{
        display: 'flex',
        flexDirection: 'column',
        gap: 0.25,
        px: 0.5,
        py: 0.25,
        borderRadius: '4px',
        borderLeft: `3px ${committed ? 'solid' : 'dashed'} ${alpha(accent, 0.7)}`,
        backgroundColor: alpha(accent, 0.06),
        opacity: isRemoving ? 0.4 : 1,
        transition: 'opacity 0.15s',
      }}
    >
      {/* Name + new/old chip + remove button */}
      <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5, justifyContent: 'space-between' }}>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5, minWidth: 0 }}>
          <Typography
            variant="caption"
            onClick={() => flashUser(assignee.UserKey)}
            title="Show this person's days & % in the capacity grid below"
            sx={{
              fontSize: '0.7rem', fontWeight: 500, color: 'text.primary', cursor: 'pointer',
              '&:hover': { textDecoration: 'underline' },
            }}
            noWrap
          >
            {assignee.displayName}
          </Typography>
          <Tooltip title={committed ? 'Already on the live schedule' : 'Draft — being added in this plan'} arrow>
            <Box
              component="span"
              sx={{
                flexShrink: 0,
                px: 0.4,
                borderRadius: '3px',
                fontSize: '0.5rem',
                fontWeight: 800,
                letterSpacing: '0.04em',
                lineHeight: 1.5,
                color: accent,
                backgroundColor: alpha(accent, 0.16),
              }}
            >
              {committed ? 'BOOKED' : 'NEW'}
            </Box>
          </Tooltip>
        </Box>
        <Tooltip title="Remove from this week">
          <span>
            <IconButton
              size="small"
              disabled={isRemoving}
              onClick={onRemove}
              sx={{
                p: 0.2,
                ml: 0.5,
                color: 'text.disabled',
                '&:hover': { color: 'error.main' },
              }}
            >
              <CloseIcon sx={{ fontSize: 11 }} />
            </IconButton>
          </span>
        </Tooltip>
      </Box>

      {/* 6C.6 — Baseline presence ghost (only when compare is ON) */}
      {showBaseline && <BaselineLabel />}
    </Box>
  );
};

// ---------------------------------------------------------------------------
// StaffingBadge — per-machine-group staffing summary (attention cue, 2026-06-21)
// ---------------------------------------------------------------------------

/**
 * Compact staffing cue for one machine group's week. We no longer compare against an
 * auto-scaled % target (it doesn't translate across multi-day / multi-week tests, Gigi
 * 2026-06-25): a machine with NOBODY this week shows an amber "add someone"; with at least
 * one person it's a calm sage "Staffed" — percentages are left to the people's own bars.
 */
const StaffingBadge: React.FC<{ staffed: boolean }> = ({ staffed }) => {
  const theme = useTheme();
  const color = staffed ? theme.schedule.employeeAlloc : theme.schedule.maintenance;
  return (
    <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.4, px: 0.25 }}>
      {staffed ? (
        <CheckCircleOutlineIcon sx={{ fontSize: 12, color }} />
      ) : (
        <WarningAmberIcon sx={{ fontSize: 12, color }} />
      )}
      <Typography
        variant="caption"
        sx={{ fontSize: '0.6rem', fontWeight: staffed ? 500 : 700, color }}
        noWrap
      >
        {staffed ? 'Staffed' : 'Unstaffed — add someone'}
      </Typography>
    </Box>
  );
};

// ---------------------------------------------------------------------------
// Component
// ---------------------------------------------------------------------------

export const Phase2WeeklyCell: React.FC<Phase2WeeklyCellProps> = ({
  task,
  weekStart,
  assignments,
  scenarioKey,
  onAssignmentsChanged,
  onAllocationChanged,
  onEscalateToDialog,
  baselineAssignments,
  compareToBaseline,
  holidays,
}) => {
  const theme = useTheme();
  const m = useWeeklyCellModel({
    task,
    weekStart,
    assignments,
    onAssignmentsChanged,
    onAllocationChanged,
    baselineAssignments,
    compareToBaseline,
    // W2-8: scopes the ×-remove stranding check to this plan's overlay.
    scenarioKey: scenarioKey ?? undefined,
    // Holiday-aware continuous staffing-window weeks (full duration — Q2).
    holidays,
  });

  // ── Render helpers ────────────────────────────────────────────────────────

  const renderAssigneeRows = (rows: TaskAssignee[]) =>
    rows.map((a) => (
      <AssigneeRow
        key={a.EmployeeAssignmentKey}
        assignee={a}
        isRemoving={m.isRemoving(a)}
        onRemove={() => m.requestRemove(a.EmployeeAssignmentKey, a.displayName, a.UserKey)}
        showBaseline={m.showBaseline(a)}
      />
    ));

  /** Picker (open) or compact "+" add button for a group. */
  const renderAdd = (
    groupId: string,
    machineBookingKey: string | null,
    groupAssignees: TaskAssignee[],
  ) =>
    m.pickerFor === groupId ? (
      <Box sx={{ mt: 0.5 }}>
        <InlineAssigneePicker
          task={task}
          assignees={groupAssignees}
          machineBookingKey={machineBookingKey}
          onAssigned={m.onPickerAssigned}
          onUnassigned={m.onPickerUnassigned}
          onEscalateToDialog={onEscalateToDialog}
          scenarioKey={scenarioKey ?? undefined}
          targetWeekStart={weekStart}
        />
      </Box>
    ) : (
      <Tooltip title="Add an employee to this machine for this week">
        <Box
          component="button"
          type="button"
          onClick={() => m.setPickerFor(groupId)}
          sx={{
            display: 'inline-flex',
            alignItems: 'center',
            gap: 0.4,
            alignSelf: 'flex-start',
            px: 0.85,
            py: 0.3,
            border: `1px dashed ${theme.schedule.employeeAlloc}`,
            borderRadius: '5px',
            backgroundColor: alpha(theme.schedule.employeeAlloc, 0.1),
            color: theme.schedule.employeeAlloc,
            fontSize: '0.66rem',
            fontWeight: 700,
            letterSpacing: '0.02em',
            lineHeight: 1.2,
            cursor: 'pointer',
            transition: 'background-color 0.12s, border-color 0.12s',
            '&:hover': {
              backgroundColor: alpha(theme.schedule.employeeAlloc, 0.22),
              borderStyle: 'solid',
            },
          }}
        >
          <AddIcon sx={{ fontSize: 15 }} />
          Add employee
        </Box>
      </Tooltip>
    );

  // Empty cell — the test isn't scheduled this week and nobody is assigned.
  if (m.isEmpty) {
    return (
      <Box
        sx={{
          minHeight: 40,
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          py: 0.5,
        }}
      >
        <Tooltip title="This test isn't scheduled this week">
          <Typography
            variant="caption"
            sx={{ color: 'text.disabled', fontSize: '0.85rem', userSelect: 'none' }}
          >
            —
          </Typography>
        </Tooltip>
      </Box>
    );
  }

  return (
    <Box sx={{ display: 'flex', flexDirection: 'column', gap: 0.75, py: 0.75, px: 0.5 }}>

      {/* One sub-group per machine booking, wrapped in a distinguished card. Under-staffed
          groups get an amber attention border + thick edge. */}
      {m.machineGroups.map(({ seg, weekAssignees, anchorKeys, under, midRunContinuous }) => (
        <Box
          key={seg.key}
          sx={{
            display: 'flex',
            flexDirection: 'column',
            gap: 0.5,
            p: 0.6,
            borderRadius: '7px',
            border: `1px solid ${under ? theme.schedule.maintenance : alpha(theme.schedule.machineTime, 0.28)}`,
            borderLeftWidth: under ? 4 : 1,
            backgroundColor: under
              ? alpha(theme.schedule.maintenance, 0.05)
              : alpha(theme.schedule.machineTime, 0.02),
            boxShadow: under ? `0 0 0 1px ${alpha(theme.schedule.maintenance, 0.22)}` : 'none',
          }}
        >
          <MachineBar
            bookingKey={seg.key}
            bookingStart={seg.bookingStart}
            bookingEnd={seg.bookingEnd}
            displayCode={seg.code}
            displayName={seg.name}
            effectiveStart={seg.effectiveStart}
            effectiveEnd={seg.effectiveEnd}
            days={seg.days}
            status={seg.status}
            assignmentKeys={anchorKeys}
            onResized={m.onBookingResized}
            holidays={holidays}
            workLabel={taskWorkLabel(task)}
            runMode={task.RunMode ?? null}
          />
          {/* Lab answer Q2 (2026-07-10): continuous staffs the FULL duration, so
              mid-run weeks get the normal staffed/unstaffed cues + Add CTA. The
              calm caption only remains for a week with no staffable working day
              (rare all-holiday lead-in — the backend would refuse staffing it). */}
          {midRunContinuous ? (
            <Typography
              variant="caption"
              sx={{ fontSize: '0.6rem', color: 'text.secondary', px: 0.25 }}
              noWrap
            >
              Runs 24/7 — no working days to staff this week
            </Typography>
          ) : (
            <StaffingBadge staffed={weekAssignees.length > 0} />
          )}
          {renderAssigneeRows(weekAssignees)}
          {!midRunContinuous && renderAdd(seg.key, seg.key, weekAssignees)}
        </Box>
      ))}

      {/* Legacy task-level group (only when ≥2 machines and such rows exist) */}
      {m.taskLevelAssignees.length > 0 && (
        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 0.5 }}>
          <Typography
            variant="caption"
            sx={{ fontSize: '0.62rem', color: 'text.disabled', fontStyle: 'italic' }}
          >
            Whole task (not machine-specific)
          </Typography>
          {renderAssigneeRows(m.taskLevelAssignees)}
        </Box>
      )}

      {/* Task-level "+" only when there's no machine in this week to staff. */}
      {m.machineGroups.length === 0 &&
        renderAdd(TASK_LEVEL_PICKER, null, m.weekAssignments)}

      {/* Confirm before removing an assignee (shared across views). */}
      {m.removeConfirmState.open && <ConfirmDialog state={m.removeConfirmState} onClose={m.cancelRemove} />}

      {/* P-17a: failed remove writes are no longer silent — red snackbar. */}
      <Snackbar open={Boolean(m.saveError)} autoHideDuration={6000} onClose={m.clearSaveError}>
        <Alert severity="error" onClose={m.clearSaveError}>{m.saveError}</Alert>
      </Snackbar>
    </Box>
  );
};
